// Aircraft kinematics: ground movement along the taxi graph (pushback, taxi, line-up, take-off roll, rollout,
// vacating) and a point-mass flight model (bank-limited turns, IAS/Mach schedule, climb and descent rates
// from the type performance, energy coupling, wind, LNAV, localizer and glide path tracking, holdings).
import type { Airport, RunwayEnd, Stand, RunwayExit } from './airport';
import type { AircraftType } from './perf';
import { climbRate } from './perf';
import type { FlightPlan } from './flightplan';
import { NM, FT, KT, DEG, bearing, angDiff, norm360, dist, clamp, ISA, alongTrack, crossTrack, type XY } from './geo';

export type GroundMode =
  | 'PARKED' | 'PUSHBACK' | 'STARTUP' | 'READY' | 'TAXI' | 'LINEUP' | 'LINED_UP' | 'TAKEOFF' | 'ROLLOUT' | 'VACATING' | 'VACATED';
export type AirMode = 'HDG' | 'LNAV' | 'LOC' | 'HOLD';

export interface Env {
  airport: Airport;
  /** Wind at an altitude (direction the wind comes from, true). */
  wind(altFt: number): { dir: number; kt: number };
  qnh: number;
  /** Position of a named fix in local metres. */
  fix(name: string): XY | null;
}

interface HoldState { fix: string; inbound: number; turn: 'L' | 'R'; phase: 'TO_FIX' | 'OUTBOUND_TURN' | 'OUTBOUND' | 'INBOUND_TURN' | 'INBOUND'; legT: number }

let nextId = 1;

export class Aircraft {
  readonly id = nextId++;
  readonly cs: string;
  x = 0;
  y = 0;
  /** Altitude above mean sea level (QNH reference), ft. */
  altFt = 0;
  /** True heading of the nose. */
  hdg = 0;
  /** Ground track (true). */
  trk = 0;
  ias = 0;
  gs = 0;
  vs = 0;
  onGround = true;
  gmode: GroundMode = 'PARKED';
  // ----- ground navigation
  path: number[] = [];
  /** Points of the current ground path (first may be the current position). */
  pts: XY[] = [];
  /** Index of the next point to reach. */
  ti = 0;
  /** Point indices at which the aircraft has to stop (hold short); removed when cleared. */
  stops = new Map<number, string>();
  holdPosition = false;
  /** Distance to an aircraft ahead on the ground (set by the world each tick). */
  trafficAhead = Infinity;
  /** Callsign of the aircraft we are waiting for on the ground. */
  trafficCs: string | null = null;
  stand: Stand | null = null;
  pushReverse = false;
  rwy: RunwayEnd | null = null;
  exit: RunwayExit | null = null;
  vacateVia: string | null = null;
  touchdownAlong = 0;
  // ----- flight targets
  amode: AirMode = 'HDG';
  tgtHdg: number | null = null;
  turnDir: 'L' | 'R' | null = null;
  /** Cleared altitude in ft QNH (flight levels converted with the current QNH). */
  tgtAltFt = 0;
  /** Assigned speed (kt IAS) – null: own speed schedule. */
  atcSpeed: number | null = null;
  route: string[] = [];
  legIdx = 0;
  ils: RunwayEnd | null = null;
  locCap = false;
  gsCap = false;
  hold: HoldState | null = null;
  goAround = false;
  /** Height above the runway at which the aircraft goes around if not cleared (set by the pilot). */
  gearDown = true;
  squawk = '2000';
  modeC = true;
  /** Position shown on the radar (updated at the sensor rate) and history dots. */
  radar = { x: 0, y: 0, alt: 0, gs: 0, trk: 0, t: -999 };
  history: XY[] = [];
  /** Departure: passed 1000 ft etc. – used by the pilot. */
  phaseFlags = new Set<string>();
  airborneT = -1;
  landedT = -1;

  constructor(readonly fp: FlightPlan, readonly type: AircraftType) {
    this.cs = fp.callsign;
  }

  get pos(): XY { return { x: this.x, y: this.y }; }
  get tas(): number { return this.onGround ? this.ias : ISA.tas(this.ias, this.altFt); }
  /** Pressure altitude in feet (what Mode C reports). */
  pressureAlt(qnh: number): number { return this.altFt + (1013.25 - qnh) * 27.3; }

  // ======================================================================= ground
  placeAtStand(st: Stand, ap: Airport): void {
    const n = ap.nodes[st.node]!;
    this.x = n.x; this.y = n.y; this.hdg = st.hdg; this.trk = st.hdg;
    this.stand = st; this.onGround = true; this.gmode = 'PARKED'; this.ias = 0; this.gs = 0;
  }

  /** Sets a ground path (graph nodes). The aircraft continues from its current position. */
  setPath(ap: Airport, nodesIn: number[], stops: Map<number, string> = new Map()): void {
    const nodes = [...nodesIn];
    this.path = nodes;
    const pts = nodes.map(id => ({ x: ap.nodes[id]!.x, y: ap.nodes[id]!.y }));
    // drop leading points behind us (we may be between nodes)
    if (pts.length > 1 && dist(this.x, this.y, pts[0]!.x, pts[0]!.y) < 3) { pts.shift(); nodes.shift(); }
    this.pts = [{ x: this.x, y: this.y }, ...pts];
    this.ti = 1;
    // stop indices refer to the node list; shift by one for the leading current-position point
    this.stops = new Map([...stops].map(([i, why]) => [i + 1, why]));
    this.holdPosition = false;
  }
  /** Remaining distance along the path to point index i. */
  distTo(i: number): number {
    if (i < this.ti) return 0;
    let d = dist(this.x, this.y, this.pts[this.ti]!.x, this.pts[this.ti]!.y);
    for (let k = this.ti; k < i; k++) d += dist(this.pts[k]!.x, this.pts[k]!.y, this.pts[k + 1]!.x, this.pts[k + 1]!.y);
    return d;
  }
  /** Next stop on the path: index and distance. */
  nextStop(): { i: number; d: number; why: string } {
    let best = { i: this.pts.length - 1, d: this.distTo(this.pts.length - 1), why: 'end' };
    for (const [i, why] of this.stops) if (i >= this.ti && i < best.i) best = { i, d: this.distTo(i), why };
    return best;
  }
  get atEnd(): boolean { return this.ti >= this.pts.length; }
  get stopped(): boolean { return this.ias < 0.5; }
  /** Stopped at a hold-short point (returns its reason). */
  waitingAt(): string | null {
    if (!this.stopped) return null;
    const s = this.nextStop();
    return s.d < 6 && s.why !== 'end' ? s.why : null;
  }
  clearStop(why: string): void { for (const [i, w] of [...this.stops]) if (w === why) this.stops.delete(i); }
  clearStopsMatching(pred: (why: string) => boolean): void { for (const [i, w] of [...this.stops]) if (pred(w)) this.stops.delete(i); }

  private groundStep(dt: number, maxKt: number, reverse = false): void {
    // speed target from the next stop, turns and traffic ahead
    const stop = this.nextStop();
    if (stop.why === 'end' && stop.d < 2.5 && !this.holdPosition) {
      const last = this.pts[this.pts.length - 1];
      if (last) { this.x = last.x; this.y = last.y; }
      this.ti = this.pts.length;
      this.ias = 0; this.gs = 0;
      return;
    }
    const decel = 1.6 * KT; // m/s²
    let tgt = maxKt;
    const remain = Math.max(0, stop.d - 1.5);
    tgt = Math.min(tgt, Math.sqrt(2 * decel * remain) / KT);
    if (this.holdPosition) tgt = 0;
    if (this.trafficAhead < Infinity) tgt = Math.min(tgt, Math.sqrt(2 * decel * Math.max(0, this.trafficAhead - 15)) / KT);
    // slow down for turns within 60 m
    let look = 0;
    for (let k = this.ti; k < this.pts.length - 1 && look < 60; k++) {
      const a = this.pts[k - 1] ?? { x: this.x, y: this.y }, b = this.pts[k]!, c = this.pts[k + 1]!;
      look += dist(this.x, this.y, b.x, b.y);
      const turn = Math.abs(angDiff(bearing(a.x, a.y, b.x, b.y), bearing(b.x, b.y, c.x, c.y)));
      const vt = turn > 60 ? 7 : turn > 35 ? 10 : turn > 18 ? 13 : maxKt;
      tgt = Math.min(tgt, vt + look / 12);
    }
    const acc = tgt > this.ias ? 0.9 : -1.6;
    this.ias = clamp(this.ias + acc * dt, tgt > this.ias ? 0 : tgt, tgt > this.ias ? tgt : this.ias);
    if (this.ias < 0.05 && tgt < 0.05) this.ias = 0;
    let move = this.ias * KT * dt;
    while (move > 0 && this.ti < this.pts.length) {
      const p = this.pts[this.ti]!;
      const d = dist(this.x, this.y, p.x, p.y);
      if (d > 0.01) {
        const brg = bearing(this.x, this.y, p.x, p.y);
        this.trk = brg;
        const want = reverse ? norm360(brg + 180) : brg;
        const dh = angDiff(this.hdg, want);
        const rate = 45 * dt; // nose wheel steering
        this.hdg = norm360(this.hdg + clamp(dh, -rate, rate));
      }
      if (d <= move) {
        this.x = p.x; this.y = p.y; move -= d; this.ti++;
        if (this.stops.has(this.ti - 1) && this.ias < 3) { this.ias = 0; break; }
      } else {
        this.x += ((p.x - this.x) / d) * move; this.y += ((p.y - this.y) / d) * move; move = 0;
      }
    }
    this.gs = this.ias;
  }

  // ======================================================================= runway
  /** Takes the runway: the path leads onto the runway, the aircraft then aligns with the runway heading. */
  startLineup(ap: Airport, end: RunwayEnd, path: number[]): void {
    this.rwy = end;
    this.gmode = 'LINEUP';
    // finish 25 m down the runway from the entry point, aligned with the centre line
    const entry = ap.nodes[path[path.length - 1]!]!;
    const along = ap.along(end, entry);
    const lat = ap.lateral(end, entry);
    const fwd = Math.max(along + 30, 25);
    const target = { x: end.start.x + Math.sin(end.hdg * DEG) * fwd, y: end.start.y + Math.cos(end.hdg * DEG) * fwd };
    void lat;
    this.setPath(ap, path);
    this.pts.push(target);
  }
  startTakeoff(): void { this.gmode = 'TAKEOFF'; }

  // ======================================================================= update
  update(dt: number, env: Env): void {
    if (this.onGround) this.updateGround(dt, env);
    else this.updateAir(dt, env);
  }

  private updateGround(dt: number, env: Env): void {
    const ap = env.airport;
    switch (this.gmode) {
      case 'PARKED': case 'STARTUP': case 'READY': case 'VACATED':
        this.ias = 0; this.gs = 0; return;
      case 'PUSHBACK':
        this.groundStep(dt, 3.5, true);
        if (this.atEnd) { this.gmode = 'STARTUP'; this.ias = 0; }
        return;
      case 'TAXI':
        this.groundStep(dt, this.onApronLane(ap) ? Math.min(12, this.type.taxiKt) : this.type.taxiKt);
        return;
      case 'LINEUP': {
        this.groundStep(dt, 8);
        if (this.atEnd && this.rwy) {
          const dh = angDiff(this.hdg, this.rwy.hdg);
          this.hdg = norm360(this.hdg + clamp(dh, -20 * dt, 20 * dt));
          if (Math.abs(dh) < 1) { this.hdg = this.rwy.hdg; this.gmode = 'LINED_UP'; this.ias = 0; }
        }
        return;
      }
      case 'LINED_UP': this.ias = 0; this.gs = 0; return;
      case 'TAKEOFF': {
        const end = this.rwy!;
        this.hdg = end.hdg; this.trk = end.hdg;
        const k = 1 - Math.min(0.35, this.ias / 400);
        this.ias += this.type.accelRwy * k * dt;
        const w = env.wind(0);
        const head = w.kt * Math.cos(angDiff(end.hdg, w.dir) * DEG);
        this.gs = Math.max(0, this.ias - head);
        this.x += Math.sin(end.hdg * DEG) * this.gs * KT * dt;
        this.y += Math.cos(end.hdg * DEG) * this.gs * KT * dt;
        if (this.ias >= this.type.vr + 4) {
          // lift-off
          this.onGround = false;
          this.altFt = end.elevFt + 5;
          this.vs = 1500;
          this.amode = 'HDG';
          this.tgtHdg = end.hdg;
          this.gmode = 'TAKEOFF';
        }
        return;
      }
      case 'ROLLOUT': {
        const end = this.rwy!;
        this.hdg = end.hdg; this.trk = end.hdg;
        const along = ap.along(end, this);
        // choose the exit (pilot's choice unless ATC specified one)
        if (!this.exit) this.exit = this.chooseExit(ap, end, along);
        const exitAlong = this.exit ? this.exit.along : end.runway.length - 40;
        const exitSpeed = this.exit && this.exit.angle < 50 ? 28 : 12;
        const d = Math.max(0, exitAlong - along - 15);
        const decel = this.type.decelRwy;
        // highest speed from which the exit speed can still be reached with normal braking
        const allowed = Math.sqrt(exitSpeed * exitSpeed + 2 * decel * (d / KT));
        if (this.ias > allowed) this.ias = Math.max(allowed, this.ias - decel * 1.4 * dt);
        else this.ias = Math.max(exitSpeed * 0.8, this.ias - decel * 0.35 * dt);
        this.gs = this.ias;
        this.x += Math.sin(end.hdg * DEG) * this.gs * KT * dt;
        this.y += Math.cos(end.hdg * DEG) * this.gs * KT * dt;
        if (this.exit && along >= this.exit.along - 3) {
          if (this.ias <= exitSpeed + 6) {
            this.gmode = 'VACATING';
            this.setPath(ap, this.exit.path.slice(1));
          } else {
            // too fast for this exit – take the next one
            const next = this.chooseExit(ap, end, along + 5);
            this.exit = next && next.along > along ? next : null;
          }
        } else if (!this.exit && along > end.runway.length - 45) {
          this.ias = 0; this.gmode = 'VACATED';
        }
        return;
      }
      case 'VACATING':
        this.groundStep(dt, 15);
        if (this.atEnd) { this.gmode = 'VACATED'; this.ias = 0; }
        return;
    }
  }

  chooseExit(ap: Airport, end: RunwayEnd, along: number): RunwayExit | null {
    // only exits whose taxiways the aircraft may use (wing span limits)
    const ex = end.exits.filter(e => e.path.every((n, i) => i === 0 || (ap.edgeBetween(e.path[i - 1]!, n) ? ap.edgeAllowed(ap.edgeBetween(e.path[i - 1]!, n)!, this.type.spanM, this.fp.airline) : true)));
    if (this.vacateVia) {
      const m = ex.find(e => e.name === this.vacateVia && e.along > along + 50);
      if (m) return m;
    }
    // first exit that can be reached at a comfortable deceleration
    const v = this.ias * KT;
    for (const e of ex) {
      if (e.along < along + 60) continue;
      const exitV = (e.angle < 50 ? 28 : 12) * KT;
      const need = (v * v - exitV * exitV) / (2 * this.type.decelRwy * KT * 0.75);
      if (e.along - along >= need) return e;
    }
    void ap;
    return ex.length ? ex[ex.length - 1]! : null;
  }

  private onApronLane(ap: Airport): boolean {
    const a = this.path[Math.max(0, this.ti - 2)], b = this.path[Math.max(0, this.ti - 1)];
    if (a === undefined || b === undefined) return false;
    const e = ap.edgeBetween(a, b);
    return !!e && (e.kind === 'lane' || e.kind === 'stand');
  }

  // ======================================================================= air
  /** Speed the pilot flies when ATC has not assigned one. */
  scheduleSpeed(env: Env, distToThrNm: number | null, arrival: boolean): number {
    const t = this.type;
    const agl = this.altFt - env.airport.meta.elevFt;
    if (this.goAround) return agl < 1500 ? t.v2 + 15 : Math.min(220, t.climbIas);
    if (!arrival) {
      if (agl < 1000) return t.v2 + 12;
      if (this.altFt < 10000) return Math.min(250, t.climbIas);
      const machIas = ISA.iasForMach(t.climbMach, this.altFt);
      return Math.min(t.climbIas, machIas);
    }
    // arrival
    if (this.locCap && distToThrNm !== null) {
      if (distToThrNm < 5) return t.vapp;
      if (distToThrNm < 8) return Math.max(t.vapp, Math.min(170, t.minClean));
      return Math.max(t.vapp + 20, Math.min(180, t.minClean));
    }
    if (distToThrNm !== null && distToThrNm < 18 && this.altFt < 7000) return Math.max(t.minClean, Math.min(220, t.descIas));
    if (this.altFt < 10000) return Math.min(250, t.descIas);
    return Math.min(t.descIas, ISA.iasForMach(t.cruiseMach - 0.02, this.altFt));
  }

  private updateAir(dt: number, env: Env): void {
    const ap = env.airport;
    const t = this.type;
    const tas = ISA.tas(this.ias, this.altFt);
    // ---------- lateral guidance
    let want: number | null = this.tgtHdg;
    const w = env.wind(this.altFt);
    const wca = (crs: number) => {
      const cross = w.kt * Math.sin(angDiff(crs, w.dir) * DEG);
      return Math.asin(clamp(cross / Math.max(60, tas), -0.5, 0.5)) / DEG;
    };
    if (this.amode === 'LNAV') {
      const fix = this.route[this.legIdx] ? env.fix(this.route[this.legIdx]!) : null;
      if (!fix) { this.amode = 'HDG'; this.tgtHdg = this.hdg; }
      else {
        const crs = bearing(this.x, this.y, fix.x, fix.y);
        want = norm360(crs + wca(crs));
        const d = dist(this.x, this.y, fix.x, fix.y);
        const next = this.route[this.legIdx + 1] ? env.fix(this.route[this.legIdx + 1]!) : null;
        let anticip = 300;
        if (next) {
          const turn = Math.abs(angDiff(crs, bearing(fix.x, fix.y, next.x, next.y)));
          const r = (tas * KT) ** 2 / (9.81 * Math.tan(25 * DEG));
          anticip = Math.min(4 * NM, r * Math.tan((turn / 2) * DEG));
        }
        if (d < Math.max(250, anticip)) {
          this.legIdx++;
          if (this.legIdx >= this.route.length) {
            if (this.hold) this.amode = 'HOLD';
            else { this.amode = 'HDG'; this.tgtHdg = want; }
          }
        }
      }
    }
    if (this.amode === 'HOLD' && this.hold) want = this.holdGuidance(dt, env, tas, wca);
    if (this.ils && (this.amode === 'HDG' || this.amode === 'LNAV' || this.amode === 'LOC')) {
      const ils = this.ils.meta.ils!;
      const thr = this.ils.thr;
      const xt = crossTrack(this, thr, ils.course); // + right of the inbound course
      const al = alongTrack(this, thr, ils.course); // negative before the threshold
      const intercept = Math.abs(angDiff(this.trk, ils.course));
      if (!this.locCap) {
        const r = (tas * KT) ** 2 / (9.81 * Math.tan(25 * DEG));
        const lead = r * (1 - Math.cos(Math.min(90, intercept) * DEG)) + 150;
        const closing = Math.sign(xt) !== Math.sign(angDiff(ils.course, this.trk)) || Math.abs(xt) < 100;
        if (al < -1.5 * NM && al > -30 * NM && intercept < 100 && Math.abs(xt) < lead && closing) { this.locCap = true; this.amode = 'LOC'; }
      }
      if (this.locCap) {
        const corr = clamp(Math.atan2(xt, 1200) / DEG * 1.2, -30, 30);
        want = norm360(ils.course - corr + wca(ils.course));
        this.turnDir = null;
      }
    }
    if (want !== null) {
      const rate = Math.min(3, (9.81 * Math.tan(25 * DEG)) / Math.max(30, tas * KT) / DEG);
      let dh = angDiff(this.hdg, want);
      if (this.turnDir && Math.abs(dh) > 5) {
        dh = this.turnDir === 'R' ? norm360(want - this.hdg) : -norm360(this.hdg - want);
      } else if (Math.abs(dh) <= 5) this.turnDir = null;
      this.hdg = norm360(this.hdg + clamp(dh, -rate * dt, rate * dt));
    }

    // ---------- vertical and speed
    const thr = this.ils?.thr ?? null;
    const dThrNm = thr ? dist(this.x, this.y, thr.x, thr.y) / NM : null;
    const arrival = this.fp.dest === ap.icao;
    let tgtIas = this.atcSpeed ?? this.scheduleSpeed(env, dThrNm, arrival);
    const minSpd = this.locCap || this.goAround ? t.vapp - 5 : this.altFt - ap.meta.elevFt < 3000 && arrival ? Math.min(t.minClean, t.vapp + 25) : t.minClean - 15;
    tgtIas = clamp(tgtIas, Math.min(minSpd, t.vapp), t.climbIas + 40);
    // glide path
    let wantVs: number;
    if (this.ils && this.locCap) {
      const ils = this.ils.meta.ils!;
      const along = -alongTrack(this, this.ils.thr, ils.course);
      const gpAlt = this.ils.elevFt + 50 + Math.tan(ils.gp * DEG) * along / FT;
      if (!this.gsCap && this.altFt >= gpAlt - 30 && along < 12 * NM && along > 0) this.gsCap = true;
      if (this.gsCap) {
        const nominal = -this.gs * 101.27 * Math.tan(ils.gp * DEG);
        wantVs = nominal + clamp((gpAlt - this.altFt) * 4, -600, 600);
        this.tgtAltFt = Math.min(this.tgtAltFt, this.altFt);
      } else wantVs = this.altitudeVs(t);
    } else wantVs = this.altitudeVs(t);
    // energy: no simultaneous steep descent and strong deceleration
    if (wantVs < -1000 && this.ias > tgtIas + 8 && !this.gsCap) wantVs = Math.max(wantVs, -1000);
    const vsAcc = 500 * dt; // fpm per second
    this.vs += clamp(wantVs - this.vs, -vsAcc, vsAcc);
    // speed change: slower while climbing hard, slower deceleration in descent
    let acc = tgtIas > this.ias ? (this.vs > 1500 ? 0.8 : 1.4) : this.vs < -500 ? -0.6 : -1.0;
    if (this.gsCap) acc = tgtIas > this.ias ? 1 : -0.9;
    this.ias += clamp(tgtIas - this.ias, -Math.abs(acc) * dt, Math.abs(acc) * dt);
    this.altFt += (this.vs / 60) * dt;
    // terrain floor: aircraft never fly into the ground (no CFIT model) – a level-off 100 ft above it
    const floor = env.airport.meta.elevFt + (this.gsCap && !this.goAround ? 0 : 100);
    if (this.altFt < floor && this.vs < 0) { this.altFt = floor; this.vs = 0; }

    // ---------- position
    const tas2 = ISA.tas(this.ias, this.altFt) * KT;
    const toward = norm360(w.dir + 180);
    const vx = Math.sin(this.hdg * DEG) * tas2 + Math.sin(toward * DEG) * w.kt * KT;
    const vy = Math.cos(this.hdg * DEG) * tas2 + Math.cos(toward * DEG) * w.kt * KT;
    this.x += vx * dt;
    this.y += vy * dt;
    this.gs = Math.hypot(vx, vy) / KT;
    this.trk = norm360(Math.atan2(vx, vy) / DEG);

    // ---------- touchdown
    if (this.ils && this.gsCap && !this.goAround) {
      const agl = this.altFt - this.ils.elevFt;
      if (agl <= 0.5) {
        this.onGround = true;
        this.altFt = this.ils.elevFt;
        this.vs = 0;
        this.gmode = 'ROLLOUT';
        this.rwy = this.ils;
        this.touchdownAlong = ap.along(this.ils, this);
        this.exit = null;
        this.ias = Math.max(t.vapp - 8, 60);
      }
    }
  }

  private altitudeVs(t: AircraftType): number {
    const d = this.tgtAltFt - this.altFt;
    if (Math.abs(d) < 10) return 0;
    if (d > 0) return Math.min(climbRate(t, this.altFt), Math.max(400, d * 2.2));
    return -Math.min(this.altFt < 10000 ? Math.min(t.rod, 2000) : t.rod, Math.max(400, -d * 2.2));
  }

  private holdGuidance(dt: number, env: Env, tas: number, wca: (crs: number) => number): number {
    const h = this.hold!;
    const fix = env.fix(h.fix);
    if (!fix) return this.hdg;
    const outbound = norm360(h.inbound + 180);
    const legSec = this.altFt > 14000 ? 90 : 60;
    switch (h.phase) {
      case 'TO_FIX': {
        const crs = bearing(this.x, this.y, fix.x, fix.y);
        if (dist(this.x, this.y, fix.x, fix.y) < 400) { h.phase = 'OUTBOUND_TURN'; this.turnDir = h.turn; }
        return norm360(crs + wca(crs));
      }
      case 'OUTBOUND_TURN':
        if (Math.abs(angDiff(this.hdg, outbound)) < 5) { h.phase = 'OUTBOUND'; h.legT = 0; }
        this.turnDir = h.turn;
        return outbound;
      case 'OUTBOUND':
        h.legT += dt;
        if (h.legT > legSec) { h.phase = 'INBOUND_TURN'; this.turnDir = h.turn; }
        return norm360(outbound + wca(outbound));
      case 'INBOUND_TURN':
        if (Math.abs(angDiff(this.hdg, h.inbound)) < 8) h.phase = 'INBOUND';
        this.turnDir = h.turn;
        return h.inbound;
      case 'INBOUND': {
        const crs = bearing(this.x, this.y, fix.x, fix.y);
        if (dist(this.x, this.y, fix.x, fix.y) < 400 || alongTrack(this, fix, h.inbound) > 0) { h.phase = 'OUTBOUND_TURN'; this.turnDir = h.turn; }
        return norm360(crs + wca(crs));
      }
    }
    void tas;
    return this.hdg;
  }

  // ======================================================================= instructions (executed by the pilot)
  flyHeading(hdgTrue: number, turn?: 'L' | 'R'): void {
    this.amode = 'HDG'; this.tgtHdg = hdgTrue; this.turnDir = turn ?? null; this.hold = null;
    if (this.locCap && !this.gsCap) this.locCap = false;
  }
  directTo(fix: string, route?: string[]): void {
    this.amode = 'LNAV'; this.route = route ?? [fix]; this.legIdx = 0; this.turnDir = null; this.hold = null; this.tgtHdg = null;
  }
  holdAt(fix: string, inbound: number, turn: 'L' | 'R'): void {
    this.hold = { fix, inbound, turn, phase: 'TO_FIX', legT: 0 };
    this.amode = 'HOLD';
  }
  clearIls(end: RunwayEnd): void { this.ils = end; this.goAround = false; }
  startGoAround(missedAltFt: number): void {
    this.goAround = true; this.locCap = false; this.gsCap = false;
    const end = this.ils;
    this.ils = null; this.amode = 'HDG'; this.tgtHdg = end ? end.hdg : this.hdg; this.tgtAltFt = missedAltFt; this.atcSpeed = null;
  }
}

/** Distance (NM) of an aircraft from a runway threshold along the final approach course. */
export function distOnFinal(ac: Aircraft, end: RunwayEnd): number {
  const crs = end.meta.ils?.course ?? end.hdg;
  return -alongTrack(ac, end.thr, crs) / NM;
}
